import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import throttle from 'lodash/throttle';
import clsx from 'clsx';
import PropTypes from 'prop-types';

import { googleSelectors } from 'reducers';
import { compileFullAddress, getFormattedAddress, parseMatches } from 'utils/helpers';
import { texts } from 'utils/languages/fr';

import {
    MemoizedButton,
    MemoizedIndexedCountriesSelect,
} from 'containers/tunnel/steps/contact-info/memoized-components/MemoizedComponents';
import { InfoButton, CustomFormLabel, CustomTextField } from 'components';
import {
    Box,
    Autocomplete,
    Grid,
    IconButton,
    InputAdornment,
    Tooltip,
    Typography,
    TextField,
} from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import * as icons from 'mdi-material-ui';
import ManualAddressDialog from './ManualAddressDialog';

const useStyles = makeStyles(theme => ({
    field: {
        width: '100%',
        flexGrow: 1,
        display: 'flex',
        flexDirection: 'column',
        padding: 0,
    },
    maxInputWidth: {
        maxWidth: theme.constants.tunnelInputWidth,
    },
    tableInput: {
        fontSize: 16,
        padding: '0 5px',
        flexGrow: 1,
    },
    inputRoot: {
        flexGrow: 1,
        marginBottom: 0,
    },
    notFound: {
        color: theme.palette.common.black,
        cursor: 'pointer',
        '&:hover': {
            color: theme.palette.primary.main,
        },
    },
}));

const AddressAutocomplete = ({
    label,
    address,
    onAddressSelected,
    onAddressInputChange,
    onAddressFocusLost,
    errors,
    showError,
    showInfoButton,
    placeholder,
    variant, // not outlined by default, can take values: ['outlined']
    inputClass,
    tableInput,
    handleToggleManual,
    ...rest
}) => {
    const classes = useStyles();
    const googlePlaces = useSelector(googleSelectors.getGoogleLibrary);
    const [autocompleteService, setAutocompleteService] = useState(null);
    const [sessionToken, setSessionToken] = useState(null);
    const [options, setOptions] = useState([]);
    const detailsRef = useRef();

    const initAutocompleteService = useCallback(() => {
        setAutocompleteService(new googlePlaces.AutocompleteService());
        setSessionToken(new googlePlaces.AutocompleteSessionToken());
    }, [googlePlaces]);

    useEffect(() => {
        if (!autocompleteService && googlePlaces) {
            initAutocompleteService();
        }
    }, [autocompleteService, initAutocompleteService, googlePlaces]);

    const fetchPlaces = React.useMemo(
        () =>
            throttle((request, callback) => {
                autocompleteService?.getPlacePredictions(request, callback);
            }, 500),
        [autocompleteService]
    );

    useEffect(() => {
        let mounted = true;

        if (!address) {
            setOptions([]);
        } else {
            fetchPlaces({ input: address, types: ['address'], sessionToken }, results => {
                if (mounted) {
                    let newOptions = [...(results || [])];
                    setOptions(newOptions);
                }
            });
        }
        return () => (mounted = false);
    }, [fetchPlaces, address, sessionToken]);

    const handleAddressInputChange = useCallback(
        (event, newValue) => onAddressInputChange(newValue),
        [onAddressInputChange]
    );

    const handleAddressSelected = useCallback(
        (event, newValue) => {
            if (newValue?.place_id) {
                const placeId = newValue.place_id;
                const placesService = new googlePlaces.PlacesService(detailsRef.current);
                placesService.getDetails(
                    { placeId, fields: ['address_components', 'formatted_address'], sessionToken },
                    result => {
                        if (!!result.address_components && !!result.formatted_address) {
                            const subpremise =
                                result.address_components.find(ac =>
                                    ac.types.includes('subpremise')
                                )?.short_name || null;

                            const route =
                                result.address_components.find(ac => ac.types.includes('route'))
                                    ?.short_name || '';

                            const streetNumber =
                                result.address_components.find(ac =>
                                    ac.types.includes('street_number')
                                )?.short_name || '';

                            const zipCode =
                                result.address_components.find(ac =>
                                    ac.types.includes('postal_code')
                                )?.short_name || '';

                            // city could be locality or administrative_area_level_3 or postal_town
                            // if either of these are missing then the user should input manually the city
                            const locality =
                                result.address_components.find(ac => ac.types.includes('locality'))
                                    ?.long_name || null;
                            const postalTown =
                                result.address_components.find(ac =>
                                    ac.types.includes('postal_town')
                                )?.long_name || null;
                            const administrativeAreaLevel3 =
                                result.address_components.find(ac =>
                                    ac.types.includes('administrative_area_level_3')
                                )?.long_name || null;
                            const city = locality || postalTown || administrativeAreaLevel3 || '';

                            const country =
                                result.address_components.find(ac => ac.types.includes('country'))
                                    ?.short_name || '';

                            const parts = result.formatted_address.split(',');
                            const routeIndex = parts.findIndex(p => p.includes(route));
                            const streetNumberIndex = parts.findIndex(p =>
                                p.includes(streetNumber)
                            );

                            let address = '';
                            if (routeIndex === streetNumberIndex && routeIndex >= 0) {
                                address = parts[routeIndex];
                            } else if (routeIndex >= 0 && streetNumberIndex >= 0) {
                                address = parts[routeIndex] + ' ' + parts[streetNumberIndex];
                            }
                            // address used in case there's no formatted address provided by google places
                            const altAddress = `${streetNumber} ${route}, ${zipCode} ${city}, ${country}`;

                            // remove subpremise from the formatted address if it is in the address_components list
                            const formattedAddr = subpremise
                                ? result.formatted_address.replace(`${subpremise}, `, '')
                                : result.formatted_address;

                            onAddressSelected({
                                address,
                                zipCode,
                                city,
                                country,
                                formattedAddress: formattedAddr || altAddress,
                            });
                        }

                        if (googlePlaces) {
                            setSessionToken(new googlePlaces.AutocompleteSessionToken());
                        }
                    }
                );
            }
        },
        [googlePlaces, onAddressSelected, sessionToken]
    );

    const renderPlaceOption = useCallback(
        (props, option) => {
            let parts = [];
            if (typeof option === 'string') {
                parts = [{ text: option, highlight: option === address }];
            } else {
                const matches = option?.structured_formatting?.main_text_matched_substrings || [];
                parts = parseMatches(
                    option?.structured_formatting?.main_text,
                    matches.map(match => [match.offset, match.offset + match.length])
                );
            }

            return (
                <Grid key={option} container alignItems="center" {...props}>
                    <Grid item xs>
                        {parts.map((part, index) => (
                            <span key={index} style={{ fontWeight: part.highlight ? 700 : 400 }}>
                                {part.text}
                            </span>
                        ))}

                        <Typography variant="body2" color="textSecondary">
                            {option?.structured_formatting?.secondary_text}
                        </Typography>
                    </Grid>
                </Grid>
            );
        },
        [address]
    );

    const isOutlined = variant === 'outlined';

    return (
        <Box display="flex" flexDirection="column">
            <div ref={detailsRef}></div>
            {label && <CustomFormLabel>{label}</CustomFormLabel>}
            <Box display="flex" alignItems="flex-end">
                <Autocomplete
                    autoComplete
                    disableClearable
                    openOnFocus
                    forcePopupIcon={false}
                    id="autocomplete-address"
                    getOptionLabel={option => {
                        return typeof option === 'string'
                            ? option
                            : option.description || option.formattedAddress;
                    }}
                    isOptionEqualToValue={(option, value) => {
                        if (!options.length) return true;
                        return (
                            !option ||
                            (typeof option === 'string'
                                ? option === value
                                : option.description === value.description)
                        );
                    }}
                    options={options}
                    clearIcon={null}
                    noOptionsText={
                        <React.Fragment>
                            <Typography>{texts.NO_OPTIONS}</Typography>
                            <Typography
                                component="span"
                                className={classes.notFound}
                                onMouseDown={handleToggleManual(true)}
                            >
                                {texts.USE_MANUAL_ADDRESS_FORM}
                            </Typography>
                        </React.Fragment>
                    }
                    value={address || null}
                    onChange={handleAddressSelected}
                    onInputChange={handleAddressInputChange}
                    onBlur={onAddressFocusLost}
                    renderInput={params => (
                        <TextField
                            ref={params.InputProps.ref}
                            classes={{
                                root: classes.inputRoot,
                                input: !isOutlined && classes.tableInput,
                            }}
                            sx={{ ...rest.sx, ...(tableInput && { border: 'none', margin: 0 }) }}
                            error={!!errors}
                            helperText={errors}
                            variant={tableInput ? 'standard' : 'outlined'}
                            disabled={rest.disabled}
                            inputProps={params.inputProps}
                            InputProps={{
                                endAdornment: tableInput ? (
                                    <InputAdornment position="start">
                                        <Tooltip title={texts.MANUAL_ADDRESS}>
                                            <IconButton
                                                size="small"
                                                disableRipple
                                                onClick={handleToggleManual(true)}
                                            >
                                                <icons.Pencil />
                                            </IconButton>
                                        </Tooltip>
                                    </InputAdornment>
                                ) : null,
                            }}
                            placeholder={placeholder}
                        />
                    )}
                    renderOption={renderPlaceOption}
                    classes={{ root: clsx(classes.field, inputClass) }}
                />
                {showInfoButton && (
                    <InfoButton
                        title={texts.ADRESSE_DU_DOMICILE_FISCAL}
                        content={texts.ADDRESS_INFO_BUTTON}
                    />
                )}
            </Box>
        </Box>
    );
};

AddressAutocomplete.propTypes = {
    address: PropTypes.any,
    onAddressSelected: PropTypes.func,
    onAddressInputChange: PropTypes.func,
    onAddressFocusLost: PropTypes.func,
    errors: PropTypes.string,
    showError: PropTypes.bool,
    showInfoButton: PropTypes.bool,
    placeholder: PropTypes.string,
    variant: PropTypes.oneOf(['outlined']),
    inputClass: PropTypes.string,
    tableInput: PropTypes.bool,
    handleToggleManual: PropTypes.func,
};

const useFullAddressStyles = makeStyles(theme => ({
    field: { width: '100%' },
    maxInputWidth: { maxWidth: theme.constants.tunnelInputWidth },
    autocomplete: { marginBottom: 16 },
    manualButton: { marginBottom: 16, maxWidth: '100%' },
}));

const _FullAddressForm = ({
    label,
    address,
    onAddressChange,
    errors,
    tunnelErrors = {},
    showInfoButton,
    noFixedWidth,
}) => {
    const classes = useFullAddressStyles();
    const [autocompleteAddress, setAutocompleteAddress] = useState({
        address: address?.address || '',
        city: address?.city || '',
        zipCode: address?.zipCode || '',
        country: address?.country || 'FR',
    });
    const [formattedAddress, setFormattedAddress] = useState(address?.formattedAddress || '');
    const [manual, setManual] = useState(false);
    const parsedErrors = useMemo(() => {
        let addressErrors = {};
        for (let key in errors) {
            if (key.includes('currentAddress')) {
                let newKey = key.replace('currentAddress,', '');
                addressErrors[newKey] = errors[key];
            }
        }
        return addressErrors;
    }, [errors]);

    const handleAddressSelected = ({
        address: addr,
        city,
        zipCode,
        country,
        formattedAddress: formattedAddr,
    }) => {
        setAutocompleteAddress({ address: addr, city, zipCode, country });
        onAddressChange({ address: addr, city, zipCode, country, formattedAddress: formattedAddr });
    };

    const handleManualChange = e => {
        const { name, value } = e.target;
        onAddressChange({
            [name]: value,
            formattedAddress: compileFullAddress({ ...address, [name]: value }),
        });
    };

    const handleAddressInputChange = inputAddress => {
        setFormattedAddress(inputAddress);
    };

    const handleToggleManual = useCallback(value => () => setManual(value), []);

    const updateFormattedAddress = useCallback(() => {
        if (address.formattedAddress || autocompleteAddress.address) {
            setFormattedAddress(
                address.formattedAddress || getFormattedAddress(autocompleteAddress)
            );
        } else {
            setFormattedAddress(address?.formattedAddress || '');
        }
    }, [autocompleteAddress, address]);

    useEffect(() => {
        updateFormattedAddress();
    }, [updateFormattedAddress]);

    if (!address) {
        return null;
    }

    return (
        <Box mb="20px" width={'100%'}>
            <AddressAutocomplete
                label={label}
                address={formattedAddress}
                handleToggleManual={handleToggleManual}
                onAddressSelected={handleAddressSelected}
                onAddressInputChange={handleAddressInputChange}
                onAddressFocusLost={updateFormattedAddress}
                errors={parsedErrors.country || parsedErrors.address || tunnelErrors.address}
                showError={!(parsedErrors.country || parsedErrors.address || tunnelErrors.address)}
                showInfoButton={showInfoButton}
                variant="outlined"
                inputClass={clsx(noFixedWidth ? '' : classes.maxInputWidth, classes.autocomplete)}
            />
            <MemoizedButton
                className={manual ? classes.manualButton : ''}
                label={texts.MANUAL_ADDRESS}
                onClick={handleToggleManual(!manual)}
            />
            {manual && (
                <Box>
                    <CustomTextField
                        name="address"
                        fullWidth
                        inputStyle={noFixedWidth ? undefined : classes.maxInputWidth}
                        label={label}
                        value={address.address}
                        onChange={handleManualChange}
                        error={parsedErrors.address || tunnelErrors.address}
                        showError={!tunnelErrors.address}
                    />
                    <CustomTextField
                        name="city"
                        fullWidth
                        inputStyle={noFixedWidth ? undefined : classes.maxInputWidth}
                        label={texts.VILLE}
                        value={address.city}
                        onChange={handleManualChange}
                        error={parsedErrors.city || tunnelErrors.city}
                        showError={!tunnelErrors.city}
                    />
                    <CustomTextField
                        name="zipCode"
                        fullWidth
                        inputStyle={noFixedWidth ? undefined : classes.maxInputWidth}
                        label={texts.CODE_POSTAL}
                        value={address.zipCode}
                        onChange={handleManualChange}
                        error={parsedErrors.zipCode || tunnelErrors.zipCode}
                        showError={!tunnelErrors.zipCode}
                    />
                    <MemoizedIndexedCountriesSelect
                        name="country"
                        fullWidth
                        className={noFixedWidth ? undefined : classes.maxInputWidth}
                        value={address.country}
                        onChange={handleManualChange}
                        error={parsedErrors.country || tunnelErrors.country}
                        showError={!tunnelErrors.country}
                    />
                </Box>
            )}
        </Box>
    );
};

const useBasicAddressStyles = makeStyles(theme => ({
    box: {
        borderBottom: `1px solid ${theme.palette.grey[600]}`,
        height: '100%',
        '&:hover': { borderBottom: `2px solid ${theme.palette.primary.main}` },
    },
    autocomplete: { marginTop: 16 },
}));

const _BasicTableCellAddressForm = ({ address, onAddressChange, placeholder }) => {
    const classes = useBasicAddressStyles();
    const [formattedAddress, setFormattedAddress] = useState(address);
    const [manualOpen, setManualOpen] = useState(false);

    const handleAddressSelected = useCallback(
        ({ formattedAddress: formattedAddr }) => {
            onAddressChange(formattedAddr);
        },
        [onAddressChange]
    );

    const handleAddressInputChange = useCallback(inputAddress => {
        setFormattedAddress(inputAddress);
    }, []);

    const handleAddressFocusLost = useCallback(() => {
        setFormattedAddress(address);
    }, [address]);

    const handleToggleManual = useCallback(value => () => setManualOpen(value), []);

    useEffect(() => {
        setFormattedAddress(address);
    }, [address]);

    return (
        <Box
            padding="0 10px"
            className={classes.box}
            display="flex"
            flexDirection="column"
            justifyContent="center"
        >
            <AddressAutocomplete
                tableInput
                handleToggleManual={handleToggleManual}
                address={formattedAddress}
                onAddressSelected={handleAddressSelected}
                onAddressInputChange={handleAddressInputChange}
                onAddressFocusLost={handleAddressFocusLost}
                placeholder={placeholder}
                inputClass={classes.autocomplete}
            />
            <ManualAddressDialog
                open={manualOpen}
                title={placeholder}
                handleClose={handleToggleManual(false)}
                onAddressChange={onAddressChange}
            />
        </Box>
    );
};

const _BasicAddressForm = ({ address, onAddressChange, placeholder, useFullAddress, ...rest }) => {
    const [formattedAddress, setFormattedAddress] = useState(address);
    const [manualOpen, setManualOpen] = useState(false);

    const handleAddressSelected = fullAddress => {
        if (useFullAddress) {
            onAddressChange(fullAddress);
        } else {
            onAddressChange(fullAddress?.formattedAddress);
        }
    };

    const handleAddressInputChange = inputAddress => {
        setFormattedAddress(inputAddress);
    };

    const handleAddressFocusLost = () => {
        setFormattedAddress(address);
    };

    const handleToggleManual = useCallback(value => () => setManualOpen(value), []);

    useEffect(() => {
        setFormattedAddress(address);
    }, [address]);

    return (
        <Box>
            <AddressAutocomplete
                tableInput
                handleToggleManual={handleToggleManual}
                address={formattedAddress}
                onAddressSelected={handleAddressSelected}
                onAddressInputChange={handleAddressInputChange}
                onAddressFocusLost={handleAddressFocusLost}
                placeholder={placeholder}
                variant="outlined"
                {...rest}
            />
            <ManualAddressDialog
                open={manualOpen}
                title={placeholder}
                handleClose={handleToggleManual(false)}
                onAddressChange={onAddressChange}
            />
        </Box>
    );
};

// this version asks the user for city and zipCode if these information is not returned by the autocomplete
// and builds a consistent string representing the formattedAddress
// also onAddressChange returns all address components in an object {address, zipCode, city, country, formattedAddress}
export const FullAddressForm = React.memo(_FullAddressForm);

// this version's onAddressChange returns only the formattedAddress string that came with the autocomplete response
// does not ask for city and zip code if this information is not in the autocomplete response
// to be used as standalone field
export const BasicAddressForm = React.memo(_BasicAddressForm);
// same as previous but to be used in a table cell
export const BasicTableCellAddressForm = React.memo(_BasicTableCellAddressForm);
